import { useMemo, useState } from "react";
import { Wrench, ChevronRight, ChevronDown } from "lucide-react";
import { formatUSD } from "../../lib/formatters";
import { allocationsSummary, costTypeSummary, toolMatchesFilters } from "../../lib/allocationsLabel";

const COST_TYPE_PILL = {
  cogs: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300",
  opex: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300",
  marketing: "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/20 dark:text-pink-300",
  mixed: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300",
  none: "bg-gray-50 text-gray-400 border-gray-200",
};

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// New "Actuals" view for the Tools tab.
//
// Auto-calculates each tool's actual monthly cost from transactions matched
// to that tool (one_off_payments rows with matchedSourceType === "tool" and
// matchedSourceId === tool.id). Replaces the legacy manual matrix where Ahmed
// had to type in monthly costs himself.
//
// Active year comes from the global dateFilter; "All time" falls back to the
// current year so the column labels make sense.
export default function ActualsMatrix({ state, dateFilter, deptFilter = "", costTypeFilter = "", onSelectTool, onOpenOneOffs }) {
  const tools = state.tools || [];
  const oneOffs = state.oneOffPayments || [];
  const offersList = state.offers || [];
  const [oneOffsExpanded, setOneOffsExpanded] = useState(false);

  const currentYear = new Date().getFullYear();
  const year = dateFilter?.year && dateFilter.year !== "all" ? dateFilter.year : currentYear;
  const fromMonth = dateFilter?.fromMonth || 1;
  const toMonth = dateFilter?.toMonth || 12;

  // Group matched expenses by toolId + monthIdx. Refunds (direction "in")
  // subtract from the tool's monthly cost so a $100 charge + $50 refund nets
  // to $50. Nothing else is excluded — every assigned invoice counts.
  const matrix = useMemo(() => {
    const map = new Map(); // toolId -> Array<number, length 12>
    for (const p of oneOffs) {
      if (p.matchedSourceType !== "tool" || !p.matchedSourceId) continue;
      if (!p.date) continue;
      const y = Number(p.date.slice(0, 4));
      if (y !== year) continue;
      const m = Number(p.date.slice(5, 7));
      if (m < fromMonth || m > toMonth) continue;
      const sign = (p.direction || "out") === "out" ? 1 : -1;
      const row = map.get(p.matchedSourceId) || new Array(12).fill(0);
      row[m - 1] += sign * (Number(p.amount) || 0);
      map.set(p.matchedSourceId, row);
    }
    return map;
  }, [oneOffs, year, fromMonth, toMonth]);

  // Tools that have at least one matched row in the active scope show up
  // by default. We sort by yearly total descending so the heavy hitters
  // sit at the top. One-off tools live in their own section below, so we
  // exclude them here.
  const rows = useMemo(() => {
    const out = [];
    for (const t of tools) {
      if (t.type === "one_off") continue;
      if (!toolMatchesFilters(t, deptFilter, costTypeFilter, offersList)) continue;
      const months = matrix.get(t.id);
      if (!months) continue;
      const yearTotal = months.reduce((a, b) => a + b, 0);
      if (yearTotal === 0) continue;
      out.push({ tool: t, months, yearTotal });
    }
    out.sort((a, b) => b.yearTotal - a.yearTotal);
    return out;
  }, [tools, matrix, deptFilter, costTypeFilter, offersList]);

  // One-off purchases: pull from BOTH sources per tool
  // 1. priceByMonth on the tool (Manual entry mode)
  // 2. matched expenses for that tool (Assign expenses mode)
  const oneOffRows = useMemo(() => {
    const out = [];
    for (const t of tools) {
      if (t.type !== "one_off") continue;
      if (!toolMatchesFilters(t, deptFilter, costTypeFilter, offersList)) continue;
      const months = new Array(12).fill(0);
      // Manual price entries
      const prices = t.priceByMonth || {};
      for (const [m, v] of Object.entries(prices)) {
        const y = Number(m.slice(0, 4));
        if (y !== year) continue;
        const idx = Number(m.slice(5, 7)) - 1;
        if (idx < 0 || idx > 11) continue;
        if (idx < fromMonth - 1 || idx > toMonth - 1) continue;
        months[idx] += Number(v) || 0;
      }
      // Matched expenses for this specific tool. Refunds (direction "in")
      // subtract from the monthly cost.
      for (const p of oneOffs) {
        if (p.matchedSourceType !== "tool" || p.matchedSourceId !== t.id) continue;
        if (!p.date) continue;
        const y = Number(p.date.slice(0, 4));
        if (y !== year) continue;
        const idx = Number(p.date.slice(5, 7)) - 1;
        if (idx < fromMonth - 1 || idx > toMonth - 1) continue;
        const sign = (p.direction || "out") === "out" ? 1 : -1;
        months[idx] += sign * (Number(p.amount) || 0);
      }
      const yearTotal = months.reduce((a, b) => a + b, 0);
      if (yearTotal === 0) continue;
      out.push({ tool: t, months, yearTotal });
    }
    out.sort((a, b) => b.yearTotal - a.yearTotal);
    return out;
  }, [tools, oneOffs, year, fromMonth, toMonth, deptFilter, costTypeFilter, offersList]);

  const oneOffMonthlyTotals = useMemo(() => {
    const arr = new Array(12).fill(0);
    for (const r of oneOffRows) {
      for (let i = 0; i < 12; i++) arr[i] += r.months[i] || 0;
    }
    return arr;
  }, [oneOffRows]);
  const oneOffYearTotal = oneOffMonthlyTotals.reduce((a, b) => a + b, 0);

  const recurringMonthlyTotals = MONTH_LABELS.map((_, i) => rows.reduce((s, r) => s + (r.months[i] || 0), 0));
  const recurringYearTotal = recurringMonthlyTotals.reduce((a, b) => a + b, 0);
  const grandMonthlyTotals = recurringMonthlyTotals.map((v, i) => v + oneOffMonthlyTotals[i]);
  const grandTotal = recurringYearTotal + oneOffYearTotal;
  const currentMonthIdx = new Date().getMonth();

  if (rows.length === 0 && oneOffYearTotal === 0) {
    return (
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card p-8 text-center">
        <Wrench size={32} className="text-brand-slate mx-auto mb-3" />
        <div className="font-medium text-brand-ink dark:text-[var(--text-primary)]">No matched tool costs for {year}</div>
        <div className="text-sm text-brand-slate mt-1 max-w-md mx-auto">
          Match expense transactions to tools from the Expenses tab (bulk action bar &rarr; Match to Tool). Once matched, monthly totals appear here automatically.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)] flex items-center justify-between">
        <div>
          <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">
            Tool actuals · {year}
            {(dateFilter?.fromMonth || dateFilter?.toMonth) && (
              <span className="text-brand-slate font-normal text-sm ml-2">
                ({MONTH_LABELS[fromMonth - 1]}{toMonth !== fromMonth ? ` – ${MONTH_LABELS[toMonth - 1]}` : ""})
              </span>
            )}
          </div>
          <div className="text-xs text-brand-slate mt-0.5">
            Auto-calculated from {rows.length} tool{rows.length === 1 ? "" : "s"} with matched transactions
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-brand-slate">Year total</div>
          <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(grandTotal)}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs table-fixed">
          <colgroup>
            <col style={{ width: "15%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "5%" }} />
            {MONTH_LABELS.map((_, i) => <col key={i} style={{ width: `${63 / 12}%` }} />)}
            <col style={{ width: "6%" }} />
          </colgroup>
          <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]">
                Tool
              </th>
              <th className="text-left px-2 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]">
                Assigned to
              </th>
              <th className="text-center px-1 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]">
                Type
              </th>
              {MONTH_LABELS.map((m, i) => {
                const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                const isCurrent = i === currentMonthIdx && year === currentYear;
                return (
                  <th
                    key={m}
                    className={`px-1 py-2 text-center font-medium uppercase tracking-wide text-[10px] ${isCurrent ? "bg-brand-blue-soft text-brand-blue-dark" : inRange ? "text-brand-slate" : "text-gray-300"}`}
                  >
                    {m}
                  </th>
                );
              })}
              <th className="px-2 py-2 text-right font-medium text-brand-slate uppercase tracking-wide text-[10px]">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ tool, months, yearTotal }) => {
              const alloc = allocationsSummary(tool, state.offers || []);
              const ct = costTypeSummary(tool);
              return (
              <tr key={tool.id} className="border-t border-gray-100 dark:border-[var(--border-soft)] hover:bg-gray-50 dark:hover:bg-[var(--bg-soft)]">
                <td
                  className="px-3 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)] cursor-pointer hover:text-brand-blue"
                  onClick={() => onSelectTool?.(tool.id)}
                  title="Open tool profile"
                >
                  {tool.name}
                </td>
                <td
                  className={`px-2 py-2 border-r border-gray-100 dark:border-[var(--border-soft)] text-[11px] truncate ${alloc.label === "—" ? "text-gray-300" : "text-brand-slate"}`}
                  title={alloc.title}
                >
                  {alloc.label}
                </td>
                <td className="px-1 py-2 text-center border-r border-gray-100 dark:border-[var(--border-soft)]" title={ct.title}>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide border ${COST_TYPE_PILL[ct.kind]}`}>
                    {ct.label}
                  </span>
                </td>
                {months.map((amount, i) => {
                  const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                  const isCurrent = i === currentMonthIdx && year === currentYear;
                  const isRefund = amount < 0;
                  const cellColor = !inRange
                    ? "text-gray-300"
                    : amount === 0
                    ? "text-gray-300"
                    : isRefund
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-brand-ink dark:text-[var(--text-primary)]";
                  return (
                    <td
                      key={i}
                      className={`px-1 py-2 text-center ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${cellColor}`}
                    >
                      {amount === 0 ? "—" : formatUSD(amount)}
                    </td>
                  );
                })}
                <td className={`px-2 py-2 text-right font-semibold border-l border-gray-100 dark:border-[var(--border-soft)] ${yearTotal < 0 ? "text-emerald-600 dark:text-emerald-400" : "text-brand-ink dark:text-[var(--text-primary)]"}`}>
                  {formatUSD(yearTotal)}
                </td>
              </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 dark:border-[var(--border-soft)] bg-gray-50/60 dark:bg-[var(--bg-soft)]/60">
              <td colSpan={3} className="px-3 py-2 font-semibold text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)]">
                Recurring subtotal
              </td>
              {recurringMonthlyTotals.map((amount, i) => {
                const isCurrent = i === currentMonthIdx && year === currentYear;
                const color = amount < 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : isCurrent
                  ? "text-brand-blue-dark"
                  : "text-brand-ink dark:text-[var(--text-primary)]";
                return (
                  <td
                    key={i}
                    className={`px-1 py-2 text-center font-semibold ${isCurrent ? "bg-brand-blue-soft/30" : ""} ${color}`}
                  >
                    {amount === 0 ? "—" : formatUSD(amount)}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-right font-semibold text-brand-ink dark:text-[var(--text-primary)] border-l border-gray-100 dark:border-[var(--border-soft)]">
                {formatUSD(recurringYearTotal)}
              </td>
            </tr>
            <tr
              className={`border-t border-gray-100 dark:border-[var(--border-soft)] bg-amber-50/40 dark:bg-amber-900/10 ${oneOffRows.length > 0 ? "cursor-pointer hover:bg-amber-100/60 dark:hover:bg-amber-900/20" : ""}`}
              onClick={oneOffRows.length > 0 ? () => setOneOffsExpanded((v) => !v) : undefined}
              title={oneOffRows.length > 0 ? (oneOffsExpanded ? "Collapse one-offs breakdown" : "Expand to see each one-off") : undefined}
            >
              <td colSpan={3} className="px-3 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)]">
                <span className="inline-flex items-center gap-1.5">
                  {oneOffRows.length > 0 && (
                    oneOffsExpanded
                      ? <ChevronDown size={12} className="text-brand-slate" />
                      : <ChevronRight size={12} className="text-brand-slate" />
                  )}
                  One-offs / Miscellaneous
                  {onOpenOneOffs && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onOpenOneOffs(); }}
                      className="ml-1 text-[10px] text-brand-blue hover:underline"
                      title="Open the One-offs sub-tab"
                    >
                      View all →
                    </button>
                  )}
                </span>
              </td>
              {oneOffMonthlyTotals.map((amount, i) => {
                const isCurrent = i === currentMonthIdx && year === currentYear;
                const color = amount === 0
                  ? "text-gray-300"
                  : amount < 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-brand-ink dark:text-[var(--text-primary)]";
                return (
                  <td
                    key={i}
                    className={`px-1 py-2 text-center ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${color}`}
                  >
                    {amount === 0 ? "—" : formatUSD(amount)}
                  </td>
                );
              })}
              <td className={`px-2 py-2 text-right font-semibold border-l border-gray-100 dark:border-[var(--border-soft)] ${oneOffYearTotal === 0 ? "text-brand-slate" : oneOffYearTotal < 0 ? "text-emerald-600 dark:text-emerald-400" : "text-brand-ink dark:text-[var(--text-primary)]"}`}>
                {oneOffYearTotal === 0 ? "—" : formatUSD(oneOffYearTotal)}
              </td>
            </tr>
            {oneOffsExpanded && oneOffRows.map(({ tool, months, yearTotal }) => {
              const alloc = allocationsSummary(tool, state.offers || []);
              const ct = costTypeSummary(tool);
              return (
              <tr key={tool.id} className="border-t border-gray-100 dark:border-[var(--border-soft)] bg-amber-50/20 dark:bg-amber-900/5">
                <td
                  className="pl-8 pr-3 py-2 text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)] cursor-pointer hover:text-brand-blue text-xs"
                  onClick={() => onSelectTool?.(tool.id)}
                  title="Open tool profile"
                >
                  {tool.name}
                </td>
                <td
                  className={`px-2 py-2 border-r border-gray-100 dark:border-[var(--border-soft)] text-[11px] truncate ${alloc.label === "—" ? "text-gray-300" : "text-brand-slate"}`}
                  title={alloc.title}
                >
                  {alloc.label}
                </td>
                <td className="px-1 py-2 text-center border-r border-gray-100 dark:border-[var(--border-soft)]" title={ct.title}>
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide border ${COST_TYPE_PILL[ct.kind]}`}>
                    {ct.label}
                  </span>
                </td>
                {months.map((amount, i) => {
                  const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                  const isCurrent = i === currentMonthIdx && year === currentYear;
                  const color = !inRange || amount === 0
                    ? "text-gray-300"
                    : amount < 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-brand-ink dark:text-[var(--text-primary)]";
                  return (
                    <td
                      key={i}
                      className={`px-1 py-2 text-center text-xs ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${color}`}
                    >
                      {amount === 0 ? "—" : formatUSD(amount)}
                    </td>
                  );
                })}
                <td className={`px-2 py-2 text-right text-xs font-medium border-l border-gray-100 dark:border-[var(--border-soft)] ${yearTotal < 0 ? "text-emerald-600 dark:text-emerald-400" : "text-brand-ink dark:text-[var(--text-primary)]"}`}>
                  {formatUSD(yearTotal)}
                </td>
              </tr>
              );
            })}
            <tr className="border-t-2 border-gray-300 dark:border-[var(--border-soft)] bg-brand-ink text-white">
              <td colSpan={3} className="px-3 py-2 font-bold border-r border-white/10">
                Grand total
              </td>
              {grandMonthlyTotals.map((amount, i) => {
                const isCurrent = i === currentMonthIdx && year === currentYear;
                const color = amount < 0
                  ? "text-emerald-300"
                  : isCurrent
                  ? "text-brand-mint"
                  : "text-white";
                return (
                  <td
                    key={i}
                    className={`px-1 py-2 text-center font-bold ${color}`}
                  >
                    {amount === 0 ? "—" : formatUSD(amount)}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-right font-bold text-white border-l border-white/10">
                {formatUSD(grandTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}